import React, { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Box,
  TextField,
  Typography,
  CircularProgress,
  Button,
  Tooltip,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import api, { API_BASE_URL } from "../../src/utils/api";

const MembersGridPage = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const limit = 100;

  useEffect(() => {
    fetchMembers(currentPage, searchTerm);
  }, [currentPage, searchTerm]);

  const fetchMembers = async (page, search) => {
    setLoading(true);
    const cacheKey = `members-page-${page}-search-${search}`;
    const cachedData = sessionStorage.getItem(cacheKey);

    if (cachedData) {
      const { members, totalRecords } = JSON.parse(cachedData);
      setMembers(members);
      setTotalRecords(totalRecords);
      setLoading(false);
      return;
    }

    try {
      const response = await api.get('/membersfilter', {
        params: { page, limit, search },
      });

      const data = response.data;
      setMembers(data.members || []);
      setTotalRecords(data.totalRecords || 0);

      // Cache the data
      sessionStorage.setItem(
        cacheKey,
        JSON.stringify({ members: data.members, totalRecords: data.totalRecords })
      );
    } catch (error) {
      console.error('Error fetching members:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const columns = [
    { field: 'CompanyCode', headerName: 'Company Code', flex: 1 },
    {
      field: 'ClubMemberCode',
      headerName: 'Club Member Number',
      flex: 1,
      renderCell: (params) => (
        <Button
          component={RouterLink}
          to={`/home/clubmembers/${params.row.ClubMemberCode}`}
          variant="text"
          size="small"
          sx={{ color: '#1976d2', fontWeight: 'bold', textTransform: 'none' }}
        >
          {params.value?.trim()}
        </Button>
      ),
    },
    {
      field: 'PhotoGraphicFileName',
      headerName: 'Photo',
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <img
          src={`${API_BASE_URL}/images/${encodeURIComponent(params.row?.PhotoGraphicFileName?.trim() || '')}`}
          alt="Member"
          onError={(e) => (e.target.src = '/img/100x100.png')}
          style={{ width: '50px', height: '50px', borderRadius: '50%' }}
        />
      ),
    },
    { field: 'LastName', headerName: 'Last Name', flex: 1 },
    { field: 'FirstName', headerName: 'First Name', flex: 1 },
    { field: 'ClubMembershipTypeCode', headerName: 'Membership Type', flex: 1 }, 
    { field: 'ClubStatusRuleCode', headerName: 'ClubStatusRuleCode ', flex: 1 },
    
    {
      field: 'EmailAddress',
      headerName: 'Email',
      flex: 1.5,
      renderCell: (params) => {
        const email = params.value?.trim();
        return email ? (
          <Tooltip title={`Send email to ${email}`}>
            <Box
              component="a"
              href={`mailto:${email}`}
              onClick={(e) => e.stopPropagation()}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#1976d2', textDecoration: 'none' }}
            >
              <EmailIcon fontSize="small" />
              <Typography variant="body2" noWrap>{email}</Typography>
            </Box>
          </Tooltip>
        ) : (
          'N/A'
        );
      },
    },
    {
      field: 'FullAddress',
      headerName: 'Address',
      flex: 2,
      renderCell: (params) => {
        const address = params.value?.trim();
        const mapsLink = address
          ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
          : null;
        return address ? (
          <Tooltip title={`View ${address} on Google Maps`}>
            <Box
              component="a"
              href={mapsLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#1976d2', textDecoration: 'none' }}
            >
              <LocationOnIcon fontSize="small" />
              <Typography variant="body2" noWrap>{address}</Typography>
            </Box>
          </Tooltip>
        ) : (
          'N/A'
        );
      },
    },
  ];

  return (
    <Box sx={{ padding: 3 }}>
      <Typography variant="h4" sx={{ marginBottom: 2 }}>
        Club Members
      </Typography>
      <Box
        sx={{
          marginBottom: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <TextField
          label="Search Members"
          variant="outlined"
          onChange={handleSearchChange}
          sx={{ width: '300px' }}
        />
        <Typography>Total Records: {totalRecords}</Typography>
      </Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
          <CircularProgress />
        </Box>
      ) : (
        <DataGrid
          rows={members}
          columns={columns}
          pageSize={100}
          getRowId={(row) => row.ClubMemberCode}
          autoHeight
          disableSelectionOnClick
          sx={{
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f5f5f5',
              color: '#000',
              fontWeight: 'bold',
            },
            '& .MuiDataGrid-cell': {
              color: '#333',
            },
          }}
        />
      )}
    </Box>
  );
};

export default MembersGridPage;
